'use client'

import { useEffect, useRef, type ReactNode } from 'react'

const TIP_PAD = 14

interface CoverageMapInteractiveProps {
  children: ReactNode
}

// Client wrapper for the (server-rendered) coverage-map SVG. Re-implements the
// original graphic's inline <script> hover/focus tooltip in React: a <script>
// injected via dangerouslySetInnerHTML never executes, so the interactivity has
// to live here. Position/content updates are written straight to the DOM (not
// React state) so we don't re-render on every mousemove. Each state
// <path class="st"> carries data-name + data-status.
export const CoverageMapInteractive = ({ children }: CoverageMapInteractiveProps) => {
  const rootRef = useRef<HTMLDivElement>(null)
  const tipRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const root = rootRef.current
    const tip = tipRef.current
    if (!root || !tip) {
      return
    }

    const show = (el: Element, x: number, y: number) => {
      const bold = document.createElement('b')
      bold.textContent = el.getAttribute('data-name') ?? ''
      tip.replaceChildren(bold, document.createTextNode(` — ${el.getAttribute('data-status') ?? ''}`))
      tip.classList.add('on')
      root.classList.add('dim')
      tip.style.left = `${Math.min(x + TIP_PAD, window.innerWidth - tip.offsetWidth - 8)}px`
      tip.style.top = `${y + TIP_PAD}px`
    }
    const hide = () => {
      tip.classList.remove('on')
      root.classList.remove('dim')
    }
    const onMove = (e: Event) => {
      const me = e as MouseEvent
      show(e.currentTarget as Element, me.clientX, me.clientY)
    }
    const onFocus = (e: Event) => {
      const el = e.currentTarget as Element
      const r = el.getBoundingClientRect()
      show(el, r.left + r.width / 2, r.top + r.height / 2)
    }

    const states = Array.from(root.querySelectorAll('.st'))
    states.forEach((el) => {
      el.addEventListener('mousemove', onMove)
      el.addEventListener('mouseleave', hide)
      el.addEventListener('focus', onFocus)
      el.addEventListener('blur', hide)
    })
    root.addEventListener('mouseleave', hide)

    return () => {
      states.forEach((el) => {
        el.removeEventListener('mousemove', onMove)
        el.removeEventListener('mouseleave', hide)
        el.removeEventListener('focus', onFocus)
        el.removeEventListener('blur', hide)
      })
      root.removeEventListener('mouseleave', hide)
    }
  }, [])

  return (
    <div ref={rootRef} className="ht-map not-prose">
      {children}
      <div ref={tipRef} className="tip" role="status" aria-live="polite" />
    </div>
  )
}
